import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useCallback, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";

import {
    Sun,
    Moon,
    Check,
    CheckCheck,
    ChevronDown,
    MessageCircle,
    Menu,
    Paperclip,
    Send,
    X,
    Download,
    FileSpreadsheet,
    Calendar,
} from "lucide-react";

import { useTheme } from "../../context/ThemeContext";

import { useAuth } from "../../context/AuthContext";

import api from "../../api/axios";

import EmployeeProfilePopup from "../employee/EmployeeProfilePopup";

import EmployeeProfileModal from "../employee/EmployeeProfileModal";
import NotificationBell from "../common/NotificationBell";
import {
    MONTH_NAMES,
    WEEKDAYS_SHORT,
    normalizeLeaveType,
    computeMonthAttendanceStats,
    computeAutoLeaveCompensation,
    countLeavesInMonth,
    getCalendarDayPresentation,
    CALENDAR_COLORS,
    CALENDAR_LEGEND_ITEMS,
    ATTENDANCE_SUMMARY_STYLES,
} from "../../utils/calendarAttendance";

const EMPLOYEE_CHAT_CACHE_KEY = "employee_chat_cache_v3";
const CORPORATE_CALENDAR_CACHE_TTL_MS = 5 * 60 * 1000;

const getCorporateCalendarCacheKey = (employeeId) => `employee_corporate_calendar_${employeeId || "self"}`;

const readCorporateCalendarCache = (employeeId) => {
    try {
        const cached = JSON.parse(localStorage.getItem(getCorporateCalendarCacheKey(employeeId)) || "null");
        if (!cached || Date.now() - Number(cached.savedAt || 0) > CORPORATE_CALENDAR_CACHE_TTL_MS) {
            return { leaves: [], attendance: [], holidays: [] };
        }
        return {
            leaves: Array.isArray(cached.leaves) ? cached.leaves : [],
            attendance: Array.isArray(cached.attendance) ? cached.attendance : [],
            holidays: Array.isArray(cached.holidays) ? cached.holidays : [],
        };
    } catch {
        return { leaves: [], attendance: [], holidays: [] };
    }
};

const writeCorporateCalendarCache = (employeeId, data) => {
    try {
        localStorage.setItem(
            getCorporateCalendarCacheKey(employeeId),
            JSON.stringify({
                leaves: Array.isArray(data?.leaves) ? data.leaves : [],
                attendance: Array.isArray(data?.attendance) ? data.attendance : [],
                holidays: Array.isArray(data?.holidays) ? data.holidays : [],
                savedAt: Date.now(),
            }),
        );
    } catch {
        // Calendar cache is a speed boost only; live data still loads normally.
    }
};

const readAttendanceRecords = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.history)) return data.history;
    return [];
};

const availabilityMeta = {
    Available: { label: "Active", dot: "bg-blue-500", text: "text-blue-600 dark:text-blue-300" },
    Leave: { label: "Leave", dot: "bg-red-500", text: "text-red-600 dark:text-red-300" },
    Active: { label: "Active", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-300" },
    Busy: { label: "Busy", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-300" },
    "Do Not Disturb": { label: "Do Not Disturb", dot: "bg-red-500", text: "text-red-600 dark:text-red-300" },
};

const availabilityOptions = ["Leave", "Active", "Busy", "Do Not Disturb"];

const normalizeAvailabilityStatus = (status) => status === "Available" ? "Active" : (status || "Active");
const normalizeTeamName = (value = "") => {
    const team = String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
    if (team === "tele marketing" || team === "telemarketing") return "telemarketing";
    if (team === "administrator") return "admin";
    return team;
};
const formatEmployeePosition = (employee = {}) => {
    const position = employee?.offerLetterDetails?.position
        || employee?.offerLetterDetails?.role
        || employee?.experienceLetterDetails?.position
        || employee?.relievingLetterDetails?.position
        || employee?.internshipLetterDetails?.internshipRole
        || employee?.internshipLetterDetails?.role
        || employee?.designation
        || employee?.position
        || employee?.role
        || employee?.team
        || "Employee";
    return String(position || "Employee").trim().toUpperCase();
};
const canAccessQuotations = (employee = {}) => {
    const team = normalizeTeamName(employee?.team);
    const role = normalizeTeamName(employee?.role);
    return team.includes("manufactur") || team === "telemarketing" || team === "admin" || role === "admin";
};

const buildBackendUrl = (path = "") => {
    if (!path || String(path).startsWith("data:") || String(path).startsWith("blob:")) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const baseURL = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const normalizedPath = String(path).startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
};

const getAvailabilityMeta = (status) => availabilityMeta[normalizeAvailabilityStatus(status)] || availabilityMeta.Active;

const AvailabilityBadge = ({ status, compact = false }) => {
    const meta = getAvailabilityMeta(status);
    return (
        <span className={`inline-flex items-center gap-1.5 ${compact ? "text-[10px]" : "text-xs"} font-black ${meta.text}`}>
            <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
};

const readStoredChatCache = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key) || "{}");
    } catch {
        sessionStorage.removeItem(key);
        return {};
    }
};

const writeStoredChatCache = (key, patch) => {
    try {
        const current = readStoredChatCache(key);
        sessionStorage.setItem(key, JSON.stringify({ ...current, ...patch, savedAt: Date.now() }));
    } catch {
        // Chat should still work if storage is disabled or full.
    }
};

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read this file. Please choose it again."));
        reader.readAsDataURL(file);
    });

const uploadChatFile = async (file) => {
    const token = localStorage.getItem("employee_token");
    const uploadBinary = async (url) => {
        const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "application/octet-stream",
                "X-File-Name": encodeURIComponent(file.name),
                "X-File-Type": file.type || "application/octet-stream",
                "X-File-Size": String(file.size),
            },
            body: file,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(`${res.status} ${data.message || res.statusText || url}`);
        return data;
    };

    const uploadJson = async (url, dataUrl) => {
        const res = await api.post(
            url,
            {
                attachment: {
                    name: file.name,
                    type: file.type || "application/octet-stream",
                    size: file.size,
                    dataUrl,
                },
            },
            { skipSaveToast: true },
        );
        return res.data;
    };

    const errors = [];
    try {
        return await uploadBinary("/api/chat/attachments/binary");
    } catch (err) {
        errors.push(`binary shared: ${err.message}`);
    }

    const dataUrl = await readFileAsDataUrl(file);
    for (const url of ["/api/chat/attachments", "/api/employee/chat/attachments"]) {
        try {
            return await uploadJson(url, dataUrl);
        } catch (err) {
            errors.push(`${url}: ${err.response?.status || ""} ${err.response?.data?.message || err.message}`);
        }
    }

    throw new Error(`File upload failed. ${errors.join(" | ")}`);
};

const buildInstantAttachmentUrl = (source, token) => {
    if (!source || String(source).startsWith("data:")) return source;
    if (String(source).startsWith("blob:")) return source;
    const backendBase = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const absoluteSource = String(source).startsWith("/api/")
        ? `${backendBase}${source}`
        : String(source);
    if (!token || !absoluteSource.includes("/api/chat/attachments/")) return absoluteSource;

    const separator = absoluteSource.includes("?") ? "&" : "?";
    return `${absoluteSource}${separator}chatToken=${encodeURIComponent(token)}`;
};

const getChatAttachmentUrl = (attachment, tokenKey) => {
    const source = attachment?.url || attachment?.dataUrl || attachment?.localUrl;
    if (!source) return "";

    const token = localStorage.getItem(tokenKey);
    return buildInstantAttachmentUrl(source, token);
};

const fetchChatAttachmentBlob = async (attachment, tokenKey, signal) => {
    const source = attachment?.url || attachment?.dataUrl || attachment?.localUrl;
    if (!source) return null;

    const token = localStorage.getItem(tokenKey);
    const headers = token && !String(source).startsWith("data:")
        ? { Authorization: `Bearer ${token}` }
        : {};
    const res = await fetch(getChatAttachmentUrl(attachment, tokenKey), {
        credentials: "include",
        headers,
        signal,
    });

    if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.message ? `Could not open file (${res.status}): ${detail.message}` : `Could not open file (${res.status})`);
    }

    return res.blob();
};

const isImageAttachment = (attachment) => String(attachment?.type || "").startsWith("image/");
const isPdfAttachment = (attachment) =>
    String(attachment?.type || "").includes("pdf")
    || String(attachment?.name || "").toLowerCase().endsWith(".pdf");
const isExcelAttachment = (attachment) => {
    const type = String(attachment?.type || "").toLowerCase();
    const name = String(attachment?.name || "").toLowerCase();
    return type.includes("spreadsheet")
        || type.includes("excel")
        || type.includes("csv")
        || [".xlsx", ".xls", ".csv"].some((extension) => name.endsWith(extension));
};

const downloadChatAttachment = async (attachment, tokenKey) => {
    const blob = await fetchChatAttachmentBlob(attachment, tokenKey);
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment?.name || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const mergeLocalSendingMessages = (serverMessages, cachedMessages = []) => {
    const serverIds = new Set(serverMessages.map((message) => message._id));
    const localMessages = cachedMessages.filter((message) =>
        (message.pending || message.failed || String(message._id || "").startsWith("temp-"))
        && !serverIds.has(message._id),
    );
    return [...serverMessages, ...localMessages];
};

const EmployeeChatPanel = () => {
    const { employee } = useAuth();
    const storedChatRef = useRef(readStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY));
    const cachedConversations = storedChatRef.current.conversations || [];
    const cachedSelected = storedChatRef.current.selectedConversation || cachedConversations[0] || null;
    const messageCacheRef = useRef(storedChatRef.current.messagesByConversation || {});
    const selectedConversationIdRef = useRef(null);
    const employeeTargetSelectedRef = useRef(false);
    const conversationRefreshTimerRef = useRef(null);
    const attachmentUploadRef = useRef({});
    const fileInputRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [conversations, setConversations] = useState(cachedConversations);
    const [directory, setDirectory] = useState(storedChatRef.current.directory || []);
    const [selectedConversation, setSelectedConversation] = useState(cachedSelected);
    const [messages, setMessages] = useState(messageCacheRef.current[cachedSelected?._id] || []);
    const [draft, setDraft] = useState("");
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [previewFullScreen, setPreviewFullScreen] = useState(false);
    const [previewFileUrl, setPreviewFileUrl] = useState("");
    const [previewFileError, setPreviewFileError] = useState("");
    const [previewFileLoading, setPreviewFileLoading] = useState(false);
    const [previewSheet, setPreviewSheet] = useState(null);
    const [attachmentLoading, setAttachmentLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState("");
    const [mobileTab, setMobileTab] = useState(cachedSelected ? "chat" : "list");

    const unreadTotal = conversations.reduce((sum, conversation) => sum + Number(conversation.unreadCount || 0), 0);
    const adminContact = directory.find((user) => user.userModel === "Admin");
    const isAdminConversation = (conversation) =>
        conversation?.participants?.some((participant) => participant.model === "Admin");
    const otherParticipant = (conversation) =>
        conversation?.participants?.find((participant) =>
            !(participant.model === "Employee" && participant.id?.toString() === employee?._id?.toString()),
        );
    const recentEmployeeConversations = conversations.filter((conversation) => !isAdminConversation(conversation));
    const employeeResults = employeeSearch.trim()
        ? directory
            .filter((user) => user.userModel === "Employee")
            .filter((user) => {
                const query = employeeSearch.trim().toLowerCase();
                return String(user.employeeId || "").toLowerCase().includes(query)
                    || String(user.name || "").toLowerCase().includes(query)
                    || String(user.email || "").toLowerCase().includes(query);
            })
            .slice(0, 6)
        : [];

    useEffect(() => {
        selectedConversationIdRef.current = selectedConversation?._id || null;
    }, [selectedConversation?._id]);

    useEffect(() => {
        if (!previewAttachment || (!isImageAttachment(previewAttachment) && !isPdfAttachment(previewAttachment) && !isExcelAttachment(previewAttachment))) {
            setPreviewFileUrl("");
            setPreviewFileError("");
            setPreviewFileLoading(false);
            setPreviewSheet(null);
            return undefined;
        }

        const controller = new AbortController();
        let objectUrl = "";
        setPreviewFileLoading(true);
        setPreviewFileError("");
        setPreviewFileUrl("");
        setPreviewSheet(null);

        fetchChatAttachmentBlob(previewAttachment, "employee_token", controller.signal)
            .then(async (blob) => {
                const url = URL.createObjectURL(blob);
                objectUrl = url;
                setPreviewFileUrl(url);
                if (isExcelAttachment(previewAttachment)) {
                    const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array" });
                    const firstSheetName = workbook.SheetNames[0];
                    const rows = firstSheetName
                        ? XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, blankrows: false })
                        : [];
                    setPreviewSheet({
                        name: firstSheetName || "Sheet 1",
                        rows: rows.slice(0, 80).map((row) => row.slice(0, 20)),
                        totalRows: rows.length,
                    });
                }
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setPreviewFileError(error.message || "Could not open this attachment.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPreviewFileLoading(false);
            });

        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [previewAttachment]);

    const fetchConversations = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/chat/conversations");
            const list = Array.isArray(res.data) ? res.data : [];
            setConversations(list);
            setSelectedConversation((current) => {
                const next = current || list[0] || null;
                writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { conversations: list, selectedConversation: next });
                return next;
            });
        } catch (err) {
            console.error("Failed to load conversations:", err);
        }
    }, []);

    const patchConversationPreview = useCallback((conversationId, patch) => {
        if (!conversationId) return;
        setConversations((current) => {
            const next = current.map((conversation) =>
                conversation._id === conversationId ? { ...conversation, ...patch } : conversation,
            );
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { conversations: next });
            return next;
        });
        setSelectedConversation((current) =>
            current?._id === conversationId ? { ...current, ...patch } : current,
        );
    }, []);

    const scheduleConversationRefresh = useCallback(() => {
        window.clearTimeout(conversationRefreshTimerRef.current);
        conversationRefreshTimerRef.current = window.setTimeout(() => {
            fetchConversations();
        }, 1200);
    }, [fetchConversations]);

    useEffect(() => () => {
        window.clearTimeout(conversationRefreshTimerRef.current);
    }, []);

    const fetchDirectory = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/chat/directory");
            const list = Array.isArray(res.data) ? res.data : [];
            setDirectory(list);
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { directory: list });
        } catch (err) {
            console.error("Failed to load directory:", err);
        }
    }, []);

    const fetchMessages = useCallback(async (conversation, options = {}) => {
        if (!conversation?._id) return;
        const conversationId = conversation._id;
        const cachedMessages = messageCacheRef.current[conversationId];
        const silent = Boolean(options.silent);
        const refreshConversations = options.refreshConversations !== false;
        const markRead = options.markRead !== false;
        if (cachedMessages) {
            setMessages(cachedMessages);
        }
        if (!silent && !cachedMessages) setLoading(true);
        try {
            const res = await api.get(`/api/employee/chat/conversations/${conversationId}/messages`, {
                cache: false,
                params: { limit: 50, markRead },
            });
            const serverList = Array.isArray(res.data) ? res.data : [];
            const list = mergeLocalSendingMessages(serverList, messageCacheRef.current[conversationId] || []);
            messageCacheRef.current[conversationId] = list;
            if (selectedConversationIdRef.current === conversationId) {
                setMessages(list);
            }
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            if (refreshConversations) fetchConversations();
        } catch (err) {
            console.error("Failed to load chat messages:", err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [fetchConversations]);

    const prefetchMessages = useCallback(async (conversation) => {
        if (!conversation?._id || messageCacheRef.current[conversation._id]) return;
        try {
            const res = await api.get(`/api/employee/chat/conversations/${conversation._id}/messages`, {
                cache: false,
                params: { limit: 50, markRead: false },
            });
            messageCacheRef.current[conversation._id] = Array.isArray(res.data) ? res.data : [];
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
        } catch (err) {
            console.error("Failed to prefetch chat messages:", err);
        }
    }, []);

    useEffect(() => {
        fetchConversations();
        const intervalMs = open ? 5000 : 10000;
        const id = window.setInterval(() => {
            if (!document.hidden) fetchConversations();
        }, intervalMs);
        return () => window.clearInterval(id);
    }, [fetchConversations, open]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            if (!document.hidden) fetchDirectory();
        }, 1500);
        return () => window.clearTimeout(id);
    }, [fetchDirectory]);

    useEffect(() => {
        if (open && directory.length === 0) fetchDirectory();
    }, [open, directory.length, fetchDirectory]);

    useEffect(() => {
        if (!open) return undefined;
        const id = window.setInterval(fetchDirectory, 10000);
        return () => window.clearInterval(id);
    }, [open, fetchDirectory]);

    useEffect(() => {
        if (!conversations.length) return;
        const selected = selectedConversation || conversations[0];
        prefetchMessages(selected);
    }, [conversations, selectedConversation, prefetchMessages]);

    useEffect(() => {
        if (!open) return undefined;
        fetchMessages(selectedConversation);
        const id = window.setInterval(
            () => fetchMessages(selectedConversation, { silent: true, refreshConversations: false }),
            3000,
        );
        return () => window.clearInterval(id);
    }, [open, selectedConversation, fetchMessages]);

    const sendMessage = async (event) => {
        event.preventDefault();
        const text = draft.trim();
        const attachmentPayload = attachment;
        if (!text && !attachmentPayload) return;
        let activeConversation = selectedConversation;
        if (!activeConversation?._id && adminContact) {
            activeConversation = await startConversation(adminContact);
        }
        if (!activeConversation?._id) {
            alert("Select Admin or search an employee before sending.");
            return;
        }
        const conversationId = activeConversation._id;
        const optimisticMessage = {
            _id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            senderModel: "Employee",
            senderName: employee?.name || "You",
            sender: { _id: employee?._id },
            message: text,
            attachment: attachmentPayload,
            readStatus: "unread",
            createdAt: new Date().toISOString(),
            pending: true,
        };
        const currentMessages = messageCacheRef.current[conversationId] || messages;
        const optimisticMessages = [...currentMessages, optimisticMessage];
        messageCacheRef.current[conversationId] = optimisticMessages;
        writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
        setMessages(optimisticMessages);
        patchConversationPreview(conversationId, {
            lastMessage: text || attachmentPayload?.name || "Attachment",
            lastMessageAt: optimisticMessage.createdAt,
        });
        setDraft("");
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        try {
            const uploadedAttachment = attachmentPayload?.uploadKey
                ? await attachmentUploadRef.current[attachmentPayload.uploadKey]
                : attachmentPayload;
            const serverAttachmentPayload = uploadedAttachment?.url && !String(uploadedAttachment.url).startsWith("blob:")
                ? {
                    name: uploadedAttachment.name,
                    type: uploadedAttachment.type,
                    size: uploadedAttachment.size,
                    storagePath: uploadedAttachment.storagePath,
                    fileKey: uploadedAttachment.fileKey,
                    url: uploadedAttachment.url,
                }
                : uploadedAttachment;
            const res = await api.post(
                `/api/employee/chat/conversations/${conversationId}/messages`,
                { message: text, attachment: serverAttachmentPayload },
                { skipSaveToast: true },
            );
            const nextMessages = (messageCacheRef.current[conversationId] || optimisticMessages)
                .map((message) => message._id === optimisticMessage._id ? res.data : message);
            messageCacheRef.current[conversationId] = nextMessages;
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(nextMessages);
            patchConversationPreview(conversationId, {
                lastMessage: res.data?.message || res.data?.attachment?.name || "Attachment",
                lastMessageAt: res.data?.createdAt || optimisticMessage.createdAt,
            });
            scheduleConversationRefresh();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to send message.";
            const failedMessages = (messageCacheRef.current[conversationId] || optimisticMessages)
                .map((message) => message._id === optimisticMessage._id ? { ...message, pending: false, failed: true, errorMessage } : message);
            messageCacheRef.current[conversationId] = failedMessages;
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(failedMessages);
            setDraft(text);
            setAttachment(attachmentPayload);
            scheduleConversationRefresh();
            console.error("Failed to send message:", err.response?.data || err);
            alert(errorMessage);
        }
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        attachChatFile(file, event);
    };

    const attachChatFile = (file, event) => {
        if (file.size > 200 * 1024 * 1024) {
            alert("Attachment must be less than 200MB.");
            if (event?.target) event.target.value = "";
            return;
        }
        const uploadKey = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const localUrl = URL.createObjectURL(file);
        const pendingAttachment = {
            name: file.name,
            type: file.type || "application/octet-stream",
            size: file.size,
            localUrl,
            uploadKey,
            uploading: true,
        };
        setAttachment(pendingAttachment);
        setAttachmentLoading(true);
        const uploadPromise = uploadChatFile(file);
        attachmentUploadRef.current[uploadKey] = uploadPromise;
        uploadPromise.then((uploaded) => {
            setAttachment((current) => current?.uploadKey === uploadKey ? uploaded : current);
        }).catch((err) => {
            setAttachment((current) => current?.uploadKey === uploadKey ? null : current);
            if (event?.target) event.target.value = "";
            if (attachmentUploadRef.current[uploadKey]) {
                alert(err.message || "File upload failed. Please try again.");
            }
        }).finally(() => {
            delete attachmentUploadRef.current[uploadKey];
            setAttachmentLoading(false);
        });
    };

    const handlePasteAttachment = (event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const fileItem = items.find((item) => item.kind === "file");
        const file = fileItem?.getAsFile();
        if (!file) return;

        event.preventDefault();
        const extension = file.type?.split("/")?.[1] || "png";
        const namedFile = new File(
            [file],
            file.name || `Screenshot-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`,
            { type: file.type || "image/png" },
        );
        attachChatFile(namedFile);
    };

    const startConversation = useCallback(async (user) => {
        try {
            const existingConversation = conversations.find((conversation) =>
                conversation.participants?.some((participant) =>
                    participant.id?.toString() === user._id?.toString()
                    && participant.model === user.userModel,
                ),
            );
            if (existingConversation) {
                setSelectedConversation(existingConversation);
                setMessages(messageCacheRef.current[existingConversation._id] || []);
                writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { selectedConversation: existingConversation });
                setMobileTab("chat");
                await fetchMessages(existingConversation, { silent: true, refreshConversations: false });
                return existingConversation;
            }

            const res = await api.post(
                "/api/employee/chat/conversations",
                { targetId: user._id, targetModel: user.userModel },
                { skipSaveToast: true },
            );
            setSelectedConversation(res.data);
            setMobileTab("chat");
            setConversations((current) => {
                const exists = current.some((conversation) => conversation._id === res.data?._id);
                const next = exists ? current : [res.data, ...current];
                writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { conversations: next, selectedConversation: res.data });
                return next;
            });
            scheduleConversationRefresh();
            await fetchMessages(res.data, { silent: true, refreshConversations: false });
            return res.data;
        } catch (err) {
            console.error("Failed to start conversation:", err);
            return null;
        }
    }, [conversations, fetchMessages, scheduleConversationRefresh]);

    const selectConversation = async (conversation, fromEmployeeList = false) => {
        employeeTargetSelectedRef.current = fromEmployeeList;
        setSelectedMessageId("");
        setSelectedConversation(conversation);
        setMessages(messageCacheRef.current[conversation._id] || []);
        writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { selectedConversation: conversation });
        setMobileTab("chat");
        await fetchMessages(conversation);
    };

    const unsendMessage = async (message) => {
        if (!selectedConversation?._id || !message?._id || String(message._id).startsWith("temp-")) return;

        const conversationId = selectedConversation._id;
        const previousMessages = messageCacheRef.current[conversationId] || messages;
        const optimisticMessages = previousMessages.map((item) =>
            item._id === message._id
                ? { ...item, message: "", attachment: null, deletedForEveryone: true, deletedAt: new Date().toISOString() }
                : item,
        );
        messageCacheRef.current[conversationId] = optimisticMessages;
        writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
        setMessages(optimisticMessages);
        setSelectedMessageId("");
        patchConversationPreview(conversationId, {
            lastMessage: "Message unsent",
            lastMessageAt: new Date().toISOString(),
        });

        try {
            const res = await api.patch(
                `/api/employee/chat/conversations/${conversationId}/messages/${message._id}/unsend`,
                {},
                { skipSaveToast: true },
            );
            const nextMessages = (messageCacheRef.current[conversationId] || optimisticMessages)
                .map((item) => item._id === message._id ? res.data : item);
            messageCacheRef.current[conversationId] = nextMessages;
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(nextMessages);
            scheduleConversationRefresh();
        } catch (err) {
            messageCacheRef.current[conversationId] = previousMessages;
            writeStoredChatCache(EMPLOYEE_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(previousMessages);
            scheduleConversationRefresh();
            alert(err.response?.data?.message || "Failed to unsend message.");
        }
    };

    useEffect(() => {
        setSelectedMessageId("");
    }, [selectedConversation?._id]);

    useEffect(() => {
        if (!open || !adminContact) return;
        if (employeeTargetSelectedRef.current) return;
        if (selectedConversation && isAdminConversation(selectedConversation)) return;
        startConversation(adminContact);
    }, [open, selectedConversation, adminContact, startConversation]);

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="relative w-11 h-11 rounded-2xl border border-[var(--border-color)] bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 flex items-center justify-center transition-all"
                title="Chat"
            >
                <MessageCircle size={18} />
                {unreadTotal > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center">
                        {unreadTotal > 9 ? "9+" : unreadTotal}
                    </span>
                )}
            </button>

            {open && createPortal(
                <div className="md:fixed md:top-[64px] md:right-4 md:left-auto md:w-[460px] md:h-[580px] md:max-h-[calc(100vh-120px)] fixed inset-x-2 top-[60px] bottom-2 h-auto max-h-[calc(100vh-80px)] w-auto bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-2xl z-[120] overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-[var(--border-color)] shrink-0 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {mobileTab === "chat" && (
                                <button
                                    type="button"
                                    onClick={() => setMobileTab("list")}
                                    className="md:hidden p-1 mr-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-secondary)]"
                                >
                                    &larr; Chats
                                </button>
                            )}
                            <div>
                                <p className="text-xs font-bold uppercase text-[var(--accent-primary)]">Chat</p>
                                <h3 className="font-bold">{selectedConversation?.title || "Admin"}</h3>
                                {!isAdminConversation(selectedConversation) && otherParticipant(selectedConversation)?.availabilityStatus && (
                                    <div className="mt-0.5">
                                        <AvailabilityBadge status={otherParticipant(selectedConversation)?.availabilityStatus} compact />
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="md:hidden p-2 rounded-xl border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[158px_minmax(0,1fr)] flex-1 min-h-0 overflow-hidden">
                        <div className={`border-r border-[var(--border-color)] overflow-y-auto ${mobileTab === "chat" ? "hidden md:block" : "block"}`}>
                            <div className="p-2 border-b border-[var(--border-color)]">
                                <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] mb-2">Default</p>
                                <button
                                    type="button"
                                    disabled={!adminContact}
                                    onClick={() => {
                                        employeeTargetSelectedRef.current = false;
                                        if (adminContact) startConversation(adminContact);
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-black border border-[var(--border-color)] ${selectedConversation?.participants?.some((participant) => participant.model === "Admin") ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]" : "bg-[var(--bg-primary)]"}`}
                                >
                                    Admin
                                </button>
                            </div>

                            <div className="p-2 border-b border-[var(--border-color)]">
                                <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] mb-2">Employee ID</p>
                                <input
                                    value={employeeSearch}
                                    onChange={(event) => setEmployeeSearch(event.target.value)}
                                    placeholder="Search ID"
                                    className="w-full h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs px-3 outline-none"
                                />
                            </div>

                            <div className="p-2 space-y-1">
                                {!employeeSearch.trim() && recentEmployeeConversations.length > 0 && (
                                    <>
                                        <p className="px-2 pb-1 text-[10px] font-black uppercase text-[var(--text-secondary)]">Recent chats</p>
                                        {recentEmployeeConversations.map((conversation) => (
                                            <button
                                                key={conversation._id}
                                                type="button"
                                                onClick={() => selectConversation(conversation, true)}
                                                className={`w-full text-left px-3 py-2 rounded-xl ${selectedConversation?._id === conversation._id ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]" : "hover:bg-[var(--bg-secondary)]"}`}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                <p className="text-xs font-black truncate">{conversation.title}</p>
                                                    {conversation.unreadCount > 0 && <span className="min-w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] font-black flex items-center justify-center">{conversation.unreadCount > 9 ? "9+" : conversation.unreadCount}</span>}
                                                </div>
                                                <div className="mt-0.5 flex items-center justify-between gap-2">
                                                    <p className="min-w-0 flex-1 truncate text-[10px] text-[var(--text-secondary)]">{conversation.lastMessage || "No messages"}</p>
                                                    {!isAdminConversation(conversation) && (
                                                        <AvailabilityBadge status={otherParticipant(conversation)?.availabilityStatus} compact />
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </>
                                )}
                                {employeeSearch.trim() && employeeResults.length === 0 && (
                                    <p className="px-2 py-3 text-[11px] text-[var(--text-secondary)]">No employee found</p>
                                )}
                                {employeeResults.map((user) => (
                                    <button
                                        key={`${user.userModel}-${user._id}`}
                                        type="button"
                                        onClick={() => {
                                            employeeTargetSelectedRef.current = true;
                                            setEmployeeSearch("");
                                            startConversation(user);
                                        }}
                                        className="w-full text-left px-3 py-2 rounded-xl hover:bg-[var(--bg-secondary)]"
                                    >
                                        <p className="text-xs font-black truncate">{user.name}</p>
                                        <div className="mt-0.5 flex items-center justify-between gap-2">
                                            <p className="min-w-0 flex-1 truncate text-[10px] text-[var(--text-secondary)]">{user.employeeId || user.email}</p>
                                            <AvailabilityBadge status={user.availabilityStatus} compact />
                                        </div>
                                    </button>
                                ))}
                            </div>

                        </div>
                        <div className={`flex min-w-0 min-h-0 flex-col bg-[var(--bg-primary)] overflow-hidden ${mobileTab === "list" ? "hidden md:flex" : "flex"}`}>
                            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-2.5">
                                {!loading && messages.length === 0 && (
                                    <p className="text-xs text-[var(--text-secondary)] text-center mt-10">No messages yet</p>
                                )}
                                {messages.map((message) => {
                                    const senderId = typeof message.sender === "object" ? message.sender?._id : message.sender;
                                    const mine = message.senderModel === "Employee" && senderId?.toString() === employee?._id?.toString();
                                    const canUnsend = mine && !message.deletedForEveryone && !message.pending && !message.failed;
                                    const selectedForAction = selectedMessageId === message._id;
                                    return (
                                        <div key={message._id} className={`flex min-w-0 ${mine ? "justify-end" : "justify-start"}`}>
                                            <div
                                                role={canUnsend ? "button" : undefined}
                                                tabIndex={canUnsend ? 0 : undefined}
                                                onClick={() => {
                                                    if (canUnsend) {
                                                        setSelectedMessageId((current) => current === message._id ? "" : message._id);
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    if (!canUnsend || (event.key !== "Enter" && event.key !== " ")) return;
                                                    event.preventDefault();
                                                    setSelectedMessageId((current) => current === message._id ? "" : message._id);
                                                }}
                                                className={`max-w-[82%] min-w-0 rounded-2xl px-3 py-2 text-xs shadow-sm transition ${mine ? "rounded-br-md bg-[var(--accent-primary)] text-white" : "rounded-bl-md bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)]"} ${canUnsend ? "cursor-pointer select-none" : ""} ${selectedForAction ? "ring-2 ring-white/80 shadow-lg" : ""}`}
                                            >
                                                {message.deletedForEveryone ? (
                                                    <p className={`italic ${mine ? "text-white/75" : "text-[var(--text-secondary)]"}`}>Message unsent</p>
                                                ) : (
                                                    message.message && <p className="whitespace-pre-wrap break-words leading-snug">{message.message}</p>
                                                )}
                                                {message.attachment && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!(message.attachment?.url || message.attachment?.dataUrl || message.attachment?.localUrl)) return;
                                                            setPreviewAttachment(message.attachment);
                                                            setPreviewFullScreen(false);
                                                        }}
                                                        disabled={!(message.attachment?.url || message.attachment?.dataUrl || message.attachment?.localUrl)}
                                                        className={`mt-2 flex max-w-full items-center gap-2 rounded-xl px-2 py-2 text-left font-bold disabled:cursor-not-allowed disabled:opacity-70 ${mine ? "bg-white/15 text-white" : "bg-[var(--card-bg)] text-[var(--accent-primary)]"}`}
                                                    >
                                                        <Paperclip size={13} className="shrink-0" />
                                                        <span className="min-w-0 flex-1 truncate">
                                                            {message.attachment.name || "Attachment"}
                                                            {!(message.attachment?.url || message.attachment?.dataUrl || message.attachment?.localUrl) ? " unavailable" : ""}
                                                        </span>
                                                    </button>
                                                )}
                                                <div className={`mt-1 flex items-center gap-1 ${mine ? "justify-end text-white/75" : "justify-start text-[var(--text-secondary)]"}`}>
                                                    <span className="text-[9px]">
                                                        {mine ? "" : `${message.senderName} - `}
                                                        {new Date(message.createdAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}
                                                    </span>
                                                    {message.pending && <span className="text-[9px]">Sending</span>}
                                                    {message.failed && <span className="text-[9px] text-red-200" title={message.errorMessage}>Failed</span>}
                                                    {mine && (message.readStatus === "read" ? <CheckCheck size={12} /> : <Check size={12} />)}
                                                </div>
                                                {canUnsend && selectedForAction && (
                                                    <div className="mt-2 flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                unsendMessage(message);
                                                            }}
                                                            className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-black text-white hover:bg-white/30"
                                                        >
                                                            Unsend
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={sendMessage} className="shrink-0 p-3 border-t border-[var(--border-color)] bg-[var(--card-bg)]">
                                {(attachment || attachmentLoading) && (
                                    <div className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-xs">
                                        <span className="min-w-0 truncate font-bold">{attachment?.uploading ? `${attachment.name} - uploading` : attachmentLoading ? "Attaching..." : attachment.name}</span>
                                        {!attachmentLoading && (
                                            <button type="button" onClick={() => setAttachment(null)} className="font-black text-red-500">Remove</button>
                                        )}
                                    </div>
                                )}
                                <div className="flex min-w-0 gap-2">
                                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachmentChange} />
                                    <button type="button" onClick={() => fileInputRef.current?.click()} className="w-10 h-10 shrink-0 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] flex items-center justify-center">
                                        <Paperclip size={16} />
                                    </button>
                                    <input
                                        value={draft}
                                        onChange={(event) => setDraft(event.target.value)}
                                        onPaste={handlePasteAttachment}
                                        disabled={!selectedConversation}
                                        placeholder={selectedConversation ? `Message ${selectedConversation.title}` : "Select chat"}
                                        className="min-w-0 flex-1 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none"
                                    />
                                    <button type="submit" className="w-10 h-10 shrink-0 rounded-xl bg-[var(--accent-primary)] text-white flex items-center justify-center disabled:opacity-50">
                                        <Send size={16} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {previewAttachment && createPortal(
                <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className={`m-auto flex flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl ${previewFullScreen ? "h-[90vh] w-[92vw] max-w-none" : "max-h-[88vh] w-full max-w-4xl"}`}>
                        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
                            <div className="min-w-0">
                                <p className="text-xs font-black uppercase text-[var(--accent-primary)]">File preview</p>
                                <h3 className="truncate text-sm font-black">{previewAttachment.name || "Attachment"}</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewAttachment(null);
                                    setPreviewFullScreen(false);
                                }}
                                className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[var(--bg-secondary)]"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-auto bg-[var(--bg-primary)] p-4">
                            {isImageAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-black text-[var(--text-secondary)]">Opening image...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <Paperclip size={28} className="mb-3" />
                                        <p className="text-sm font-black">{previewFileError}</p>
                                    </div>
                                ) : (
                                    <img
                                        src={previewFileUrl}
                                        alt={previewAttachment.name || "Attachment"}
                                        className={`mx-auto max-w-full rounded-xl object-contain ${previewFullScreen ? "max-h-[76vh]" : "max-h-[68vh]"}`}
                                    />
                                )
                            ) : isPdfAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-black text-[var(--text-secondary)]">Opening PDF...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <Paperclip size={28} className="mb-3" />
                                        <p className="text-sm font-black">{previewFileError}</p>
                                    </div>
                                ) : (
                                    <iframe
                                        title={previewAttachment.name || "PDF preview"}
                                        src={previewFileUrl}
                                        className={`w-full rounded-xl border border-[var(--border-color)] bg-white ${previewFullScreen ? "h-[76vh]" : "h-[68vh]"}`}
                                    />
                                )
                            ) : isExcelAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-black text-[var(--text-secondary)]">Opening Excel sheet...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <FileSpreadsheet size={34} className="mb-3" />
                                        <p className="text-sm font-black">{previewFileError}</p>
                                    </div>
                                ) : previewSheet?.rows?.length ? (
                                    <div className="h-full w-full overflow-auto rounded-xl border border-[var(--border-color)] bg-white">
                                        <div className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-black text-slate-700">
                                            {previewSheet.name}{previewSheet.totalRows > previewSheet.rows.length ? ` - showing first ${previewSheet.rows.length} rows` : ""}
                                        </div>
                                        <table className="min-w-full border-collapse text-left text-xs text-slate-900">
                                            <tbody>
                                                {previewSheet.rows.map((row, rowIndex) => (
                                                    <tr key={rowIndex} className={rowIndex === 0 ? "bg-slate-100 font-black" : ""}>
                                                        {row.map((cell, cellIndex) => (
                                                            <td key={cellIndex} className="max-w-[220px] border border-slate-200 px-3 py-2 align-top">
                                                                <span className="block truncate">{String(cell ?? "")}</span>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
                                        <FileSpreadsheet size={34} className="mb-3 text-emerald-500" />
                                        <p className="text-sm font-black">{previewAttachment.name || "Excel sheet"}</p>
                                        <p className="mt-1 text-xs text-[var(--text-secondary)]">No sheet data found.</p>
                                    </div>
                                )
                            ) : (
                                <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
                                    <Paperclip size={28} className="mb-3 text-[var(--accent-primary)]" />
                                    <p className="text-sm font-black">{previewAttachment.name || "Attachment"}</p>
                                    <p className="mt-1 text-xs text-[var(--text-secondary)]">
                                        Preview is not available for this file type inside the app.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-color)] px-4 py-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewAttachment(null);
                                    setPreviewFullScreen(false);
                                }}
                                className="rounded-xl bg-[var(--bg-secondary)] px-4 py-2 text-xs font-black text-[var(--text-primary)]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreviewFullScreen((current) => !current)}
                                className="rounded-xl border border-[var(--border-color)] px-4 py-2 text-xs font-black text-[var(--text-primary)]"
                            >
                                {previewFullScreen ? "Exit full view" : "Full view"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    downloadChatAttachment(previewAttachment, "employee_token").catch((error) => {
                                        alert(error.message || "Could not download this file.");
                                    });
                                }}
                                className="inline-flex items-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 py-2 text-xs font-black text-white"
                            >
                                <Download size={14} />
                                Download
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const EmployeeNavbar = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const {
        employee,
        setEmployee,
    } = useAuth();

    const {
        theme,
        toggleTheme,
    } = useTheme();

    const [loggingOut, setLoggingOut] =
        useState(false);

    const [profileOpen, setProfileOpen] =
        useState(false);

    const [
        profileModalOpen,
        setProfileModalOpen,
    ] = useState(false);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [availabilityOpen, setAvailabilityOpen] = useState(false);
    const [teamMenuOpen, setTeamMenuOpen] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const availabilityRef = useRef(null);
    const teamMenuRef = useRef(null);
    const moreMenuRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        api.get("/api/settings")
            .then((res) => {
                if (!cancelled) setCompanyLogoUrl(res.data?.logo_url || null);
            })
            .catch(() => {
                // Keep the bundled logo when the company setting is unavailable.
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
        setProfileOpen(false);
        setShowCalendarModal(false);
        setAvailabilityOpen(false);
        setTeamMenuOpen(false);
        setMoreMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (availabilityRef.current && !availabilityRef.current.contains(event.target)) {
                setAvailabilityOpen(false);
            }
            if (teamMenuRef.current && !teamMenuRef.current.contains(event.target)) {
                setTeamMenuOpen(false);
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
                setMoreMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const hasProfileValue = (value) => {
        return value !== undefined && value !== null && String(value).trim() !== "";
    };

    const isProfileComplete = (emp) => {
        if (!emp) return true;

        return [
            emp.email,
            emp.phone,
            emp.address,
            emp.aadharNumber,
            emp.dob,
            emp.gender,
            emp.panNumber,
            emp.bankDetails?.accountNumber,
            emp.bankDetails?.ifscCode,
        ].every(hasProfileValue);
    };

    const incomplete = employee && !isProfileComplete(employee);
    const canViewLunchAlerts = employee?.role === "lead" || employee?.role === "hr";
    const showQuotations = canAccessQuotations(employee);

    const updateAvailabilityStatus = async (nextStatus) => {
        if (!employee || normalizeAvailabilityStatus(employee.availabilityStatus) === nextStatus) return;
        const previousEmployee = employee;
        const nextEmployee = { ...employee, availabilityStatus: nextStatus };
        setEmployee(nextEmployee);
        try {
            const res = await api.put(
                "/api/employee/profile/me",
                { availabilityStatus: nextStatus },
                {
                    params: { minimal: true },
                    skipSaveToast: true,
                },
            );
            if (res.data) {
                setEmployee({
                    ...nextEmployee,
                    ...res.data,
                    bankDetails: {
                        ...(nextEmployee.bankDetails || {}),
                        ...(res.data.bankDetails || {}),
                    },
                });
            }
        } catch (err) {
            setEmployee(previousEmployee);
            console.error("Failed to update availability status:", err);
        }
    };

    // ================= LOGOUT =================
    const handleLogout = async () => {

        if (loggingOut) return;

        setLoggingOut(true);

        setProfileOpen(false);

        try {

            await api.post(
                "/api/employee/auth/logout",
            );

        } catch (err) {

            console.error(
                "Employee logout failed:",
                err,
            );

        } finally {

            localStorage.removeItem("employee_token");

            setEmployee(null);

            navigate("/login", {
                replace: true,
            });

            setLoggingOut(false);
        }
    };

    // ================= PROFILE =================
    const handleOpenProfile = () => {

        setProfileOpen(false);

        setProfileModalOpen(true);
    };

    // ================= NAV STYLES =================
    const linkBase =
        `
        px-2.5
        xl:px-3
        py-2

        rounded-2xl

        text-xs
        2xl:text-sm
        font-bold
        whitespace-nowrap

        transition-all
        duration-150
        `;

    const activeClass =
        `
        bg-[var(--accent-primary)]/15
        text-[var(--accent-primary)]

        shadow-sm
        `;

    const inactiveClass =
        `
        text-[var(--text-secondary)]

        hover:text-[var(--text-primary)]

        hover:bg-black/5
        dark:hover:bg-white/5
        `;

    return (
        <>
            {/* ================= NAVBAR ================= */}
            <div
                className="
                sticky
                top-0
                z-50

                backdrop-blur-xl

                bg-[var(--card-bg)]/80
                dark:bg-[#0f0f10]/80

                border-b
                border-[var(--border-color)]

                shadow-[var(--shadow-soft)]

                transition-all
                duration-300
            "
            >

                <div
                    className="
                    px-3
                    max-[420px]:px-2.5
                    md:px-6
                    py-2.5
                    sm:py-3

                    flex
                    justify-between
                    items-center
                "
                >

                    {/* ================= LEFT ================= */}
                    <div
                        className="
                        flex
                        items-center
                        gap-3
                        max-[420px]:gap-2
                        min-w-0
                        flex-1
                    "
                    >

                        {/* LOGO */}
                        <div className="shrink-0 cursor-pointer ml-2" onClick={() => navigate('/dashboard')}>
                            <img 
                                src={companyLogoUrl || "/logo_light.png?v=3"}
                                alt="Savrion Technologies" 
                                className="h-[40px] w-auto max-w-[180px] object-contain dark:hidden"
                            />
                            <img 
                                src={companyLogoUrl || "/logo_dark.png?v=3"}
                                alt="Savrion Technologies" 
                                className="h-[40px] w-auto max-w-[180px] object-contain hidden dark:block"
                            />
                        </div>

                        {/* LINKS - DESKTOP ONLY */}
                        <div
                            className="
                            hidden
                            xl:flex
                            items-center
                            gap-1
                            min-w-0
                            flex-1
                            overflow-visible
                            employee-desktop-nav-pill
                            rounded-3xl
                            bg-black/[0.025]
                            p-1
                            [scrollbar-width:none]
                            dark:bg-white/[0.035]
                            [&::-webkit-scrollbar]:hidden
                        "
                        >

                            <NavLink
                                to="/dashboard"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Dashboard
                            </NavLink>

                            <NavLink
                                to="/attendance"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Attendance
                            </NavLink>

                            <NavLink
                                to="/eod"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                EOD Report
                            </NavLink>

                            <NavLink
                                to="/leave"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Leaves
                            </NavLink>

                            <NavLink
                                to="/expenses"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Expenses
                            </NavLink>

                            <NavLink
                                to="/tasks"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Tasks
                            </NavLink>

                            {showQuotations && (
                                <NavLink
                                    to="/quotations"
                                    className={({
                                        isActive,
                                    }) =>
                                        `${linkBase} ${isActive
                                            ? activeClass
                                            : inactiveClass
                                        }`
                                    }
                                >
                                    Quotations
                                </NavLink>
                            )}

                            <NavLink
                                to="/complaints"
                                className={({
                                    isActive,
                                }) =>
                                    `${linkBase} ${isActive
                                        ? activeClass
                                        : inactiveClass
                                    }`
                                }
                            >
                                Grievance
                            </NavLink>

                            {/* ================= LEAD ONLY ================= */}
                            {employee?.role ===
                                "lead" && (
                                    <div ref={teamMenuRef} className="relative shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => setTeamMenuOpen((current) => !current)}
                                            className={`${linkBase} inline-flex items-center gap-1.5 ${
                                                location.pathname.startsWith("/team")
                                                    ? activeClass
                                                    : inactiveClass
                                            }`}
                                        >
                                            Team
                                            <ChevronDown
                                                size={14}
                                                className={`transition-transform ${teamMenuOpen ? "rotate-180" : ""}`}
                                            />
                                        </button>

                                        {teamMenuOpen && (
                                            <div className="absolute right-0 top-[40px] z-[150] w-48 overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-1.5 shadow-2xl">
                                                <NavLink
                                                    to="/team/attendance"
                                                    className={({ isActive }) =>
                                                        `block rounded-xl px-3 py-2.5 text-sm font-extrabold transition-all ${
                                                            isActive
                                                                ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                                : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                                        }`
                                                    }
                                                >
                                                    Team Attendance
                                                </NavLink>
                                                <NavLink
                                                    to="/team/eod"
                                                    className={({ isActive }) =>
                                                        `block rounded-xl px-3 py-2.5 text-sm font-extrabold transition-all ${
                                                            isActive
                                                                ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                                : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                                        }`
                                                    }
                                                >
                                                    Team EOD
                                                </NavLink>
                                                <NavLink
                                                    to="/team/leave"
                                                    className={({ isActive }) =>
                                                        `block rounded-xl px-3 py-2.5 text-sm font-extrabold transition-all ${
                                                            isActive
                                                                ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                                : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                                        }`
                                                    }
                                                >
                                                    Team Leaves
                                                </NavLink>
                                            </div>
                                        )}
                                    </div>
                                )}

                            <div ref={moreMenuRef} className="relative shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setMoreMenuOpen((current) => !current)}
                                    className={`${linkBase} inline-flex items-center gap-1.5 ${
                                        ["/offboarding", "/policies", "/announcements", "/lunch-alerts"].some((path) =>
                                            location.pathname.startsWith(path),
                                        )
                                            ? activeClass
                                            : inactiveClass
                                    }`}
                                >
                                    More
                                    <ChevronDown
                                        size={14}
                                        className={`transition-transform ${moreMenuOpen ? "rotate-180" : ""}`}
                                    />
                                </button>

                                {moreMenuOpen && (
                                    <div className="absolute right-0 top-[40px] z-[150] w-52 overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-1.5 shadow-2xl">
                                        {[
                                            { to: "/offboarding", label: "Offboarding" },
                                            { to: "/policies", label: "Policies" },
                                            { to: "/announcements", label: "Announcements" },
                                            ...(canViewLunchAlerts ? [{ to: "/lunch-alerts", label: "Lunch Alerts" }] : []),
                                        ].map((item) => (
                                            <NavLink
                                                key={item.to}
                                                to={item.to}
                                                className={({ isActive }) =>
                                                    `block rounded-xl px-3 py-2.5 text-sm font-extrabold transition-all ${
                                                        isActive
                                                            ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                            : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                                    }`
                                                }
                                            >
                                                {item.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>

                    </div>

                    {/* ================= RIGHT ================= */}
                    <div
                        className="
                        relative

                        flex
                        items-center
                        gap-1.5
                        shrink-0
                        max-[420px]:gap-1
                        sm:gap-2
                        md:gap-3
                            "
                        >
                        {/* ================= THEME BUTTON ================= */}
                        <button
                            onClick={toggleTheme}
                            className="
                                hidden
                                sm:flex
                                w-10
                                h-10
                                2xl:w-11
                                2xl:h-11

                                rounded-2xl

                                border
                                border-[var(--border-color)]

                                items-center
                                justify-center

                                bg-black/5
                                hover:bg-black/10

                                dark:bg-white/5
                                dark:hover:bg-white/10

                                transition-all
                                duration-300
                            "
                        >

                            {theme ===
                                "dark" ? (
                                <Sun
                                    size={18}
                                    className="
                                        text-yellow-400
                                    "
                                />
                            ) : (
                                <Moon
                                    size={18}
                                    className="
                                        text-gray-700
                                    "
                                />
                            )}

                        </button>

                        <div className="employee-nav-action">
                            <NotificationBell />
                        </div>
                        <div className="employee-nav-action">
                            <EmployeeChatPanel />
                        </div>

                        {/* ================= MOBILE MENU BUTTON ================= */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="
                                xl:hidden
                                w-10
                                h-10
                                max-[420px]:h-9
                                max-[420px]:w-9
                                sm:w-11
                                sm:h-11
                                rounded-2xl
                                border
                                border-[var(--border-color)]
                                flex
                                items-center
                                justify-center
                                bg-black/5
                                dark:bg-white/5
                                transition-all
                            "
                        >
                            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>

                        {/* ================= CALENDAR BUTTON ================= */}
                        <button
                            onClick={() => setShowCalendarModal(true)}
                            className="
                                hidden
                                md:flex
                                items-center
                                justify-center
                                hover:bg-black/5
                                dark:hover:bg-white/5
                                w-10
                                h-10
                                2xl:w-11
                                2xl:h-11
                                rounded-2xl
                                border
                                border-[var(--border-color)]
                                text-[var(--text-primary)]
                                transition-all
                                duration-300
                            "
                            title="View Calendar"
                        >
                            <Calendar size={18} />
                        </button>

                        {/* ================= AVAILABILITY STATUS ================= */}
                        <div ref={availabilityRef} className="relative hidden md:block">
                            <button
                                type="button"
                                onClick={() => setAvailabilityOpen((current) => !current)}
                                className="flex h-11 min-w-[178px] items-center justify-between gap-3 rounded-2xl border border-[var(--border-color)] bg-black/5 px-3 text-left transition-all hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10"
                                title="Availability status"
                            >
                                <AvailabilityBadge status={employee?.availabilityStatus} />
                                <ChevronDown
                                    size={15}
                                    className={`text-[var(--text-secondary)] transition-transform ${availabilityOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {availabilityOpen && (
                                <div className="absolute right-0 top-[48px] z-[150] w-56 overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-1.5 shadow-2xl">
                                    {availabilityOptions.map((status) => {
                                        const meta = getAvailabilityMeta(status);
                                        const activeStatus = normalizeAvailabilityStatus(employee?.availabilityStatus);
                                        const active = activeStatus === status;
                                        return (
                                            <button
                                                type="button"
                                                key={status}
                                                onClick={() => {
                                                    setAvailabilityOpen(false);
                                                    updateAvailabilityStatus(status);
                                                }}
                                                className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-black transition-all ${
                                                    active
                                                        ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                        : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                                }`}
                                            >
                                                <span className={`inline-flex items-center gap-2 ${meta.text}`}>
                                                    <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                                                    {meta.label}
                                                </span>
                                                {active && <Check size={15} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ================= PROFILE BUTTON ================= */}
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() =>
                                setProfileOpen(
                                    (
                                        prev,
                                    ) =>
                                        !prev,
                                )
                            }
                            title={incomplete ? "Profile incomplete" : "Profile"}
                            className="
                                flex
                                items-center
                                gap-2
                                sm:gap-3

                                hover:bg-black/5
                                dark:hover:bg-white/5

                                px-1.5
                                py-1.5
                                max-[420px]:px-0.5
                                sm:px-3
                                sm:py-2

                                rounded-2xl

                                transition-all
                                duration-300
                            "
                        >

                            {/* AVATAR */}
                            <div
                                className="
                                relative
                                w-10
                                h-10
                                max-[420px]:h-9
                                max-[420px]:w-9
                                sm:w-11
                                sm:h-11

                                rounded-2xl

                                bg-[var(--accent-primary)]

                                flex
                                items-center
                                justify-center

                                text-sm
                                font-bold

                                text-black

                                shadow-sm
                            "
                            >
                                {employee?.name?.charAt(0)?.toUpperCase()}
                                
                                {incomplete && (
                                    <>
                                        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-400 rounded-full border-2 border-white dark:border-[#0f0f10]" />
                                        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-400 rounded-full animate-ping" />
                                    </>
                                )}
                            </div>

                            <div
                                className="
                                hidden
                                lg:block
                                text-left
                            "
                            >

                                <div
                                    className="
                                    text-sm
                                    text-[var(--text-primary)]

                                    font-semibold
                                "
                                >
                                    {employee?.name ||
                                        "User"}
                                </div>

                                <div className="mt-0.5 flex max-w-[260px] flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <span className="whitespace-normal break-words text-xs leading-tight text-[var(--text-secondary)]" title={formatEmployeePosition(employee)}>
                                        {formatEmployeePosition(employee)}
                                    </span>
                                    <AvailabilityBadge status={employee?.availabilityStatus} compact />
                                </div>

                            </div>

                        </button>

                        {/* ================= PROFILE POPUP ================= */}
                        <EmployeeProfilePopup
                            open={
                                profileOpen
                            }
                            onClose={() =>
                                setProfileOpen(
                                    false,
                                )
                            }
                            employee={
                                employee
                            }
                            onLogout={
                                handleLogout
                            }
                            onOpenProfile={
                                handleOpenProfile
                            }
                        />

                    </div>

                </div>

                {/* ================= MOBILE MENU ================= */}
                {mobileMenuOpen && (
                    <div className="xl:hidden border-t border-[var(--border-color)] bg-[var(--card-bg)] p-2.5 shadow-xl dark:bg-[#111111] sm:p-4">
                        <div className="grid max-h-[calc(100vh-82px)] grid-cols-1 gap-2 overflow-y-auto min-[380px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        <NavLink to="/dashboard" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Dashboard
                        </NavLink>
                        <NavLink to="/attendance" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Attendance
                        </NavLink>
                        <NavLink to="/eod" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            EOD Report
                        </NavLink>
                        <NavLink to="/leave" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Leaves
                        </NavLink>
                        <NavLink to="/expenses" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Expenses
                        </NavLink>
                        <NavLink to="/tasks" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Tasks
                        </NavLink>
                        {showQuotations && (
                            <NavLink to="/quotations" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                                Quotations
                            </NavLink>
                        )}
                        <NavLink to="/complaints" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Grievance
                        </NavLink>
                        <NavLink to="/offboarding" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Offboarding
                        </NavLink>
                        <NavLink to="/policies" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Policies
                        </NavLink>
                        <NavLink to="/announcements" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                            Announcements
                        </NavLink>
                        {canViewLunchAlerts && (
                            <NavLink to="/lunch-alerts" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                                Lunch Alerts
                            </NavLink>
                        )}
                        {employee?.role === "lead" && (
                            <>
                                <NavLink to="/team/attendance" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                                    Team Attendance
                                </NavLink>
                                <NavLink to="/team/eod" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                                    Team EOD
                                </NavLink>
                                <NavLink to="/team/leave" onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `${linkBase} block ${isActive ? activeClass : inactiveClass}`}>
                                    Team Leaves
                                </NavLink>
                            </>
                        )}
                        </div>
                    </div>
                )}

            </div>

            {/* ================= PROFILE MODAL ================= */}
            <EmployeeProfileModal
                open={profileModalOpen}
                onClose={() =>
                    setProfileModalOpen(
                        false,
                    )
                }
            />

            {/* ================= CALENDAR SIDEBAR ================= */}
            {showCalendarModal && (
                <div className="fixed inset-0 z-[150] flex justify-end">
                    {/* Backdrop */}
                    <div onClick={() => setShowCalendarModal(false)} className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-300" />
                    
                    {/* Sidebar container: quarter length width */}
                    <div className="relative w-full sm:w-[24rem] h-full bg-[var(--card-bg)] dark:bg-[#111111] border-l border-neutral-200 dark:border-neutral-800 shadow-2xl p-6 flex flex-col justify-between transition-transform duration-300 z-10 animate-slide-in overflow-y-auto">
                        
                        <div>
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
                                <div>
                                    <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-50">Corporate Calendar</h3>
                                    <p className="text-xs text-neutral-400 dark:text-neutral-500">Track company holidays & leaves</p>
                                </div>
                                <button onClick={() => setShowCalendarModal(false)} className="w-8 h-8 rounded-lg bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center justify-center transition-all">
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Inline Calendar Render */}
                            <CalendarWidget />
                        </div>

                        {/* Slide Animation Styling */}
                        <style dangerouslySetInnerHTML={{__html: `
                            @keyframes slideIn {
                                from { transform: translateX(100%); }
                                to { transform: translateX(0); }
                            }
                            .animate-slide-in {
                                animation: slideIn 0.3s ease-out forwards;
                            }
                        `}} />
                    </div>
                </div>
            )}
        </>
    );
};

const CalendarWidget = () => {
    const today = new Date();
    const [currentDate, setCurrentDate] = useState(new Date());
    const { employee, fetchEmployee } = useAuth();
    const employeeId = employee?._id || employee?.id || "self";
    const [calendarData, setCalendarData] = useState(() => readCorporateCalendarCache(employeeId));
    const myLeaves = calendarData.leaves;
    const attendanceHistory = calendarData.attendance;
    const holidays = calendarData.holidays;
    
    useEffect(() => {
        const requestStamp = Date.now();
        const cached = readCorporateCalendarCache(employeeId);
        if (cached.leaves.length || cached.attendance.length) {
            setCalendarData(cached);
        }

        const leavesRequest = api.get('/api/employee/leave/my', {
            cache: false,
            params: { limit: 370, _t: requestStamp },
        }).then(res => Array.isArray(res.data) ? res.data : []);

        const attendanceRequest = api.get('/api/employee/attendance/summary', {
            cache: false,
            params: { limit: 370, _t: requestStamp },
        })
            .then(res => readAttendanceRecords(res.data))
            .catch(() => {
                return api.get('/api/employee/attendance/my', {
                    cache: false,
                    params: { limit: 370, _t: requestStamp },
                })
                    .then(res => readAttendanceRecords(res.data));
            });

        const holidaysRequest = api.get('/api/holidays/public', {
            cache: false,
            params: { _t: requestStamp },
        }).then(res => Array.isArray(res.data) ? res.data : []);

        Promise.all([leavesRequest, attendanceRequest, holidaysRequest])
            .then(([leaves, attendance, holidays]) => {
                const nextData = { leaves, attendance, holidays };
                setCalendarData(nextData);
                writeCorporateCalendarCache(employeeId, nextData);
            })
            .catch(err => console.error(err));

        if (fetchEmployee) {
            fetchEmployee().catch(() => {});
        }
    }, [employeeId, fetchEmployee]);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    const { presentCount, notInCount, weekOffCount, holidayCount, dayMeta } =
        computeMonthAttendanceStats(year, month, {
            leaves: myLeaves,
            attendance: attendanceHistory,
            holidays,
            today,
            workingDays: employee?.workingDays,
            attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
        });
    const autoCompensation = computeAutoLeaveCompensation({ employee, dayMeta });

    const {
        casualCount,
        sickCount,
        lopCount,
        compensatoryCount,
        emergencyCount,
        otherCount,
        totalLeaveDays,
    } = countLeavesInMonth(myLeaves, year, month);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };
    
    const handleNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
    };

    const days = [];
    
    // Previous month fill days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push({
            day: prevMonthDays - i,
            isCurrentMonth: false,
            key: `prev-${i}`
        });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        const isToday = i === today.getDate() && month === today.getMonth() && year === today.getFullYear();
        const meta = dayMeta[i] || {};

        days.push({
            day: i,
            isCurrentMonth: true,
            isToday,
            isSunday: meta.isSunday,
            isWeekOff: meta.isWeekOff,
            isHoliday: meta.isHoliday,
            holidayName: meta.holidayName,
            leave: meta.leave,
            isPresent: meta.isPresent,
            isNotIn: meta.isNotIn,
            isCompensated: meta.isCompensated,
            autoLeaveType: meta.isCompensated
                ? (meta.compensationType === "medical" ? "sick" : meta.compensationType)
                : autoCompensation.allocationByDate?.[meta.dateKey],
            key: `curr-${i}`,
        });
    }
    
    // Next month fill days
    const totalSlots = 42;
    const remainingSlots = totalSlots - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
        days.push({
            day: i,
            isCurrentMonth: false,
            key: `next-${i}`
        });
    }

    const activeMonthHolidays = (holidays || [])
        .map((holiday) => {
            const dateKey = String(holiday?.dateKey || holiday?.date || "").slice(0, 10);
            const parsed = /^\d{4}-\d{2}-\d{2}$/.test(dateKey)
                ? new Date(`${dateKey}T00:00:00`)
                : new Date(holiday?.date);
            if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() !== year || parsed.getMonth() !== month) return null;
            return {
                day: parsed.getDate(),
                name: holiday.name || holiday.title || "Public Holiday",
                dateKey: `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.day - b.day);

    return (
        <div className="w-full flex flex-col gap-6">
            <div>
                {/* Header controls */}
                <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-base text-neutral-800 dark:text-neutral-100">
                        {MONTH_NAMES[month]} {year}
                    </span>
                    <div className="flex gap-1.5">
                        <button onClick={handlePrevMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-xs font-bold border border-neutral-200 dark:border-neutral-700">&lt;</button>
                        <button onClick={handleNextMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-xs font-bold border border-neutral-200 dark:border-neutral-700">&gt;</button>
                    </div>
                </div>

                {/* Weekdays */}
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-neutral-400 dark:text-neutral-500 mb-2">
                    {WEEKDAYS_SHORT.map(d => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1 text-center text-xs">
                    {days.map(item => {
                        const { cellStyle, titleText } = getCalendarDayPresentation(item);
                        const todayAttendance = item.isToday && !item.isWeekOff && !item.isHoliday
                            ? (item.isCompensated ? "compensated" : item.isPresent ? "present" : "absent")
                            : "";

                        return (
                            <div 
                                key={item.key} 
                                className={`relative py-2 rounded-lg font-semibold flex items-center justify-center transition-all ${cellStyle}`}
                                title={todayAttendance ? `${titleText} • ${todayAttendance === "present" ? "Present" : todayAttendance === "compensated" ? "Compensated" : "Absent (no check-in)"}` : titleText}
                            >
                                {item.day}
                                {todayAttendance && (
                                    <span
                                        className={`absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full text-[8px] font-black text-white shadow-sm ${
                                            todayAttendance === "present" ? "bg-emerald-600" : todayAttendance === "compensated" ? "bg-violet-600" : "bg-red-500"
                                        }`}
                                        aria-label={todayAttendance === "present" ? "Present today" : todayAttendance === "compensated" ? "Compensated today" : "Absent today"}
                                    >
                                        {todayAttendance === "present" ? "P" : todayAttendance === "compensated" ? "C" : "A"}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* LEGEND SECTION */}
            <div className="mt-2 border-t border-neutral-100 dark:border-neutral-800 pt-4 flex flex-col gap-3">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">Legend</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                    {CALENDAR_LEGEND_ITEMS.map(({ key, label }) => {
                        const legendLabel =
                            key === "casual" ? "Casual Leave (CL)"
                            : key === "sick" ? "Medical Leave (ML)"
                            : key === "compensatory" ? "Comp Off (CO)"
                            : key === "lop" ? "Loss of Pay (LOP)"
                            : label;
                        return (
                            <div key={key} className="flex items-center gap-1.5">
                                <span className={`w-2.5 h-2.5 rounded-full shadow-md ${CALENDAR_COLORS[key].dot}`} />
                                <span className="text-neutral-600 dark:text-neutral-300 font-medium">{legendLabel}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ATTENDANCE SUMMARY */}
            <div className="mt-2 border-t border-neutral-100 dark:border-neutral-800 pt-4 flex flex-col gap-3">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">
                    Attendance Summary ({MONTH_NAMES[month]} {year})
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.present.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.present.text}`}>Present</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.present.badge}`}>{presentCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.notIn.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.notIn.text}`}>Absent</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.notIn.badge}`}>{notInCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.weekOff.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.weekOff.text}`}>Week Off</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.weekOff.badge}`}>{weekOffCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.holiday.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.holiday.text}`}>Holidays</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.holiday.badge}`}>{holidayCount}d</span>
                    </div>
                </div>
            </div>

            {/* MONTHLY HOLIDAYS LIST */}
            <div className="mt-2 border-t border-neutral-100 dark:border-neutral-800 pt-4 flex flex-col gap-3">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">Holidays this month</h4>
                {activeMonthHolidays.length === 0 ? (
                    <p className="text-xs italic text-neutral-400 dark:text-neutral-500">No public holidays this month.</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {activeMonthHolidays.map((h, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs p-2 rounded-xl bg-neutral-50 dark:bg-neutral-800/20 border border-neutral-100 dark:border-neutral-800">
                                <span className="font-bold text-neutral-700 dark:text-neutral-300">{h.day} {MONTH_NAMES[month].substring(0, 3)}</span>
                                <span className="font-medium text-emerald-600 dark:text-emerald-400">{h.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MONTHLY LEAVE TRACKER COUNTER */}
            {(() => (
                    <div className="mt-2 border-t border-neutral-100 dark:border-neutral-800 pt-4 flex flex-col gap-3">
                        <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">Leave & Absence Summary (This Month)</h4>
                        {totalLeaveDays === 0 && notInCount === 0 ? (
                            <p className="text-xs italic text-neutral-400 dark:text-neutral-500">No leaves or absences this month.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                {autoCompensation.counts.carryForward > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
                                        <span className="font-semibold text-emerald-700 dark:text-emerald-400">Carry Forward</span>
                                        <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white font-bold">{autoCompensation.counts.carryForward}d</span>
                                    </div>
                                )}
                                {autoCompensation.counts.casual > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30">
                                        <span className="font-semibold text-blue-700 dark:text-blue-400">Auto Casual</span>
                                        <span className="px-2 py-0.5 rounded-md bg-blue-600 text-white font-bold">{autoCompensation.counts.casual}d</span>
                                    </div>
                                )}
                                {autoCompensation.counts.medical > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30">
                                        <span className="font-semibold text-indigo-700 dark:text-indigo-400">Auto Medical</span>
                                        <span className="px-2 py-0.5 rounded-md bg-indigo-600 text-white font-bold">{autoCompensation.counts.medical}d</span>
                                    </div>
                                )}
                                {autoCompensation.counts.compensatory > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/30">
                                        <span className="font-semibold text-purple-700 dark:text-purple-400">Auto Comp Off</span>
                                        <span className="px-2 py-0.5 rounded-md bg-violet-600 text-white font-bold">{autoCompensation.counts.compensatory}d</span>
                                    </div>
                                )}
                                {autoCompensation.payableAbsentCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
                                        <span className="font-semibold text-red-700 dark:text-red-400">LOP</span>
                                        <span className="px-2 py-0.5 rounded-md bg-red-600 text-white font-bold">{autoCompensation.payableAbsentCount}d</span>
                                    </div>
                                )}
                                {casualCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30">
                                        <span className="font-semibold text-blue-700 dark:text-blue-400">Casual Leave</span>
                                        <span className="px-2 py-0.5 rounded-md bg-blue-600 text-white font-bold">{casualCount}d</span>
                                    </div>
                                )}
                                {sickCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 border border-neutral-800 dark:border-neutral-200">
                                        <span className="font-semibold">Medical Leave</span>
                                        <span className="px-2 py-0.5 rounded-md bg-white text-black dark:bg-black dark:text-white font-bold">{sickCount}d</span>
                                    </div>
                                )}
                                {compensatoryCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/30">
                                        <span className="font-semibold text-purple-700 dark:text-purple-400">Comp Off</span>
                                        <span className="px-2 py-0.5 rounded-md bg-violet-600 text-white font-bold">{compensatoryCount}d</span>
                                    </div>
                                )}
                                {lopCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
                                        <span className="font-semibold text-red-700 dark:text-red-400">Loss of Pay</span>
                                        <span className="px-2 py-0.5 rounded-md bg-red-600 text-white font-bold">{lopCount}d</span>
                                    </div>
                                )}
                                {emergencyCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30">
                                        <span className="font-semibold text-amber-700 dark:text-amber-400">Emergency</span>
                                        <span className="px-2 py-0.5 rounded-md bg-amber-500 text-neutral-950 font-bold">{emergencyCount}d</span>
                                    </div>
                                )}
                                {otherCount > 0 && (
                                    <div className="flex items-center justify-between p-2 rounded-xl bg-gray-50 dark:bg-gray-800/20 border border-gray-100 dark:border-gray-800">
                                        <span className="font-semibold text-gray-700 dark:text-gray-400">Other</span>
                                        <span className="px-2 py-0.5 rounded-md bg-gray-600 text-white font-bold">{otherCount}d</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {totalLeaveDays === 0 && notInCount > 0 && (
                            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 italic">
                                Auto compensation uses carry forward, casual, medical, and comp off before LOP.
                            </p>
                        )}
                    </div>
            ))()}

            {/* Detailed monthly leave tracker intentionally removed from the employee calendar. */}
            {false && (() => {
                let totalUsedCL = 0;
                let totalUsedML = 0;
                let totalUsedLOP = 0;
                let totalUsedComp = 0;

                myLeaves.forEach(l => {
                    if (l.status === 'approved') {
                        const start = new Date(l.startDate);
                        const end = new Date(l.endDate);
                        
                        // Count days in current month & year
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                            if (d.getFullYear() === year && d.getMonth() === month) {
                                const leaveType = normalizeLeaveType(l.leaveType);
                                if (leaveType === 'casual') totalUsedCL++;
                                else if (leaveType === 'sick') totalUsedML++;
                                else if (leaveType === 'lop') totalUsedLOP++;
                                else if (leaveType === 'compensatory') totalUsedComp++;
                            }
                        }
                    }
                });

                // Calculate Sunday check-ins shown as earned comp-off activity for this month.
                let earnedCompLeaves = 0;
                attendanceHistory.forEach(record => {
                    if (record.date && record.checkIn) {
                        const parts = record.date.split('-');
                        if (parts.length === 3) {
                            const yearNum = parseInt(parts[0], 10);
                            const monthNum = parseInt(parts[1], 10) - 1;
                            const dayNum = parseInt(parts[2], 10);
                            const dateObj = new Date(yearNum, monthNum, dayNum);
                            
                            // Only Sunday is week off in this app, so only Sunday check-ins count here.
                            if (dateObj.getFullYear() === year && dateObj.getMonth() === month && dateObj.getDay() === 0) {
                                earnedCompLeaves++;
                            }
                        }
                    }
                });

                const clBalance = employee?.casualLeave ?? 0;
                const mlBalance = employee?.medicalLeave ?? 0;
                const compBalance = Number(employee?.compensatoryLeave || 0);
                const lopBalance = employee?.lossOfPay ?? 0;
                const carriedForwardCL = Number(employee?.carriedForwardCasualLeave || 0);
                const carriedForwardML = Number(employee?.carriedForwardMedicalLeave || 0);
                const carriedForwardTotal = carriedForwardCL + carriedForwardML;

                const clQuota = clBalance + totalUsedCL;
                const mlQuota = mlBalance + totalUsedML;
                const compQuota = compBalance + totalUsedComp;

                return (
                    <div className="mt-2 border-t border-neutral-100 dark:border-neutral-800 pt-4 flex flex-col gap-3">
                        <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">Monthly Leave Tracker ({MONTH_NAMES[month]} {year})</h4>
                        <div className="flex flex-col gap-3">
                            <div className="p-3 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/10 border border-emerald-100 dark:border-emerald-900/25 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-emerald-700 dark:text-emerald-400">Carry Forward</span>
                                    <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white font-extrabold text-[10px]">{carriedForwardTotal}d</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-emerald-50 dark:border-emerald-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Used</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-100">0d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-emerald-50 dark:border-emerald-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Balance</span>
                                        <span className="text-sm font-black text-emerald-700 dark:text-emerald-400">{carriedForwardTotal}d</span>
                                    </div>
                                </div>
                            </div>

                            {/* Casual Leave Card */}
                            <div className="p-3 rounded-2xl bg-blue-50/50 dark:bg-blue-950/10 border border-blue-100 dark:border-blue-900/20 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-blue-700 dark:text-blue-400">Casual Leave (CL)</span>
                                    <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white font-extrabold text-[10px]">Quota: {clQuota}d</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-blue-50 dark:border-blue-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Used</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-100">{totalUsedCL}d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-blue-50 dark:border-blue-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Balance</span>
                                        <span className="text-sm font-black text-blue-600 dark:text-blue-400">{clBalance}d</span>
                                    </div>
                                </div>
                            </div>

                            {/* Medical Leave Card */}
                            <div className="p-3 rounded-2xl bg-neutral-50 dark:bg-neutral-800/20 border border-neutral-200 dark:border-neutral-800 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-neutral-800 dark:text-neutral-100">Medical Leave (ML)</span>
                                    <span className="px-2 py-0.5 rounded-full bg-neutral-950 dark:bg-neutral-50 dark:text-neutral-950 text-white font-extrabold text-[10px]">Quota: {mlQuota}d</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-neutral-100 dark:border-neutral-800 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Used</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-100">{totalUsedML}d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-neutral-100 dark:border-neutral-800 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Balance</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-200">{mlBalance}d</span>
                                    </div>
                                </div>
                            </div>

                            {/* Compensatory Off Card */}
                            <div className="p-3 rounded-2xl bg-purple-50/50 dark:bg-purple-950/10 border border-purple-100 dark:border-purple-900/20 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-purple-700 dark:text-purple-400">Compensatory Off (Comp Off)</span>
                                    <span className="px-2 py-0.5 rounded-full bg-purple-600 text-white font-extrabold text-[10px]">Quota: {compQuota}d</span>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center">
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-purple-50 dark:border-purple-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Earned</span>
                                        <span className="text-sm font-black text-purple-600 dark:text-purple-400">{earnedCompLeaves}d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-purple-50 dark:border-purple-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Used</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-100">{totalUsedComp}d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-purple-50 dark:border-purple-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Balance</span>
                                        <span className="text-sm font-black text-purple-600 dark:text-purple-400">{compBalance}d</span>
                                    </div>
                                </div>
                            </div>

                            {/* Loss of Pay Card */}
                            <div className="p-3 rounded-2xl bg-red-50/50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/20 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-red-700 dark:text-red-400">Loss of Pay (LOP)</span>
                                    <span className="px-2 py-0.5 rounded-full bg-red-600 text-white font-extrabold text-[10px]">Uncapped</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-red-50 dark:border-red-950 flex flex-col">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Used</span>
                                        <span className="text-sm font-black text-neutral-800 dark:text-neutral-100">{totalUsedLOP}d</span>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-900/60 p-2 rounded-xl border border-red-50 dark:border-red-950 flex flex-col justify-center items-center">
                                        <span className="text-[10px] text-neutral-400 font-bold uppercase">Current LOP</span>
                                        <span className="text-sm font-black text-red-600 dark:text-red-400">{lopBalance}d</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default EmployeeNavbar;
